/**
 * The airship's deck props (ADR 0033): the jetty's mooring post and the netted
 * cargo on deck, each built at its local origin for a placement of the scene
 * record (`ship_mooring_post`, `ship_cargo`). Lifted from brad-airship, where
 * `data/ship.json` listed them as `props` and two builders drew them: the post
 * by `airship-world.js` (the post, its iron collars, the cross-beam, the coiled
 * line and the lamp block) and `airship-exterior.js` (more rings, the trim
 * cleats and their balls), the cargo by `airship-exterior.js` (crates lashed
 * under a net to ring bolts). One builder each now; the numbers are the
 * prototype's, relative to the prop's centre.
 */
import * as THREE from 'three';
import { exteriorMaterials } from './exterior.ts';
import { type ShipKit, type Vec3, box, primitive, tagMesh } from './kit.ts';

/**
 * The mooring post on the jetty (part `mooring`: it stays behind when the ship casts off). `size.h` is the record's
 * height; the lamp block sits 9 cm below it (3.42 of the prototype's 3.51).
 */
export const buildMooringPost = (kit: ShipKit, size: { readonly w: number; readonly d: number; readonly h: number }): THREE.Group => {
  exteriorMaterials(kit);
  const root = new THREE.Group();
  root.name = 'ship-mooring-post';
  const cylinder = (key: string, y: number, r: number, h: number): THREE.Mesh => primitive(root, kit.get(key), new THREE.CylinderGeometry(r, r, h, 8), 0, y, 0);
  // `airship-world.js`: the post, its collars, the cross-beam, the coiled line and the lamp block.
  cylinder('timber', 1.65, 0.29, 3.3);
  cylinder('trim', 0.25, 0.36, 0.3);
  cylinder('trim', 2.8, 0.33, 0.18);
  box(root, kit.get('timber'), 0, 2.65, 0, 2.1, 0.22, 0.25);
  for (let i = 0; i < 4; i += 1) {
    const g = new THREE.TorusGeometry(0.4 + i * 0.06, 0.035, 4, 20);
    g.rotateX(Math.PI / 2);
    primitive(root, kit.get('warm'), g, 0, 0.14 + i * 0.035, 0);
  }
  box(root, kit.get('warm'), 0, size.h - 0.09, 0, 0.32, 0.18, 0.32);
  // `airship-exterior.js`: the rope turns higher up, and a cleat with a brass ball each side.
  for (let i = 0; i < 5; i += 1) {
    const g = new THREE.TorusGeometry(0.31, 0.035, 4, 16);
    g.rotateX(Math.PI / 2);
    primitive(root, kit.get('warm'), g, 0, 0.72 + i * 0.08, 0);
  }
  for (const side of [-1, 1]) {
    box(root, kit.get('trim'), side * 0.31, 1.35, 0, 0.08, 0.66, 0.21);
    primitive(root, kit.get('warm'), new THREE.SphereGeometry(0.063, 6, 4), side * 0.365, 1.55, 0);
  }
  root.traverse((o) => {
    if (o instanceof THREE.Mesh) tagMesh(o, { part: 'mooring' });
  });
  return root;
};

/** A taut line from a to b (no wind: a `ropeTaut` lashing). */
const lashing = (root: THREE.Object3D, kit: ShipKit, a: Vec3, b: Vec3, r: number): void => {
  const av = new THREE.Vector3(...a);
  const bv = new THREE.Vector3(...b);
  const v = bv.clone().sub(av);
  const g = new THREE.CylinderGeometry(r, r, v.length(), 4, 6, true);
  g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.clone().normalize()));
  const c = av.add(bv).multiplyScalar(0.5);
  g.translate(c.x, c.y, c.z);
  primitive(root, kit.get('ropeTaut'), g);
};

/** Cargo on its record-authored footprint: crates lashed under a net to ring bolts, on a dunnage board. */
export const buildCargo = (kit: ShipKit, size: { readonly w: number; readonly d: number }): THREE.Group => {
  exteriorMaterials(kit);
  const root = new THREE.Group();
  root.name = 'ship-cargo';
  const B = (key: string, x: number, y: number, z: number, w: number, h: number, d: number): THREE.Mesh => box(root, kit.get(key), x, y, z, w, h, d);
  B('wale', 0, 0.04, 0, size.w, 0.08, size.d);
  for (const [dx, y, dz, w, h, d] of [
    [-0.48, 0.5, -0.45, 0.95, 0.9, 0.95],
    [0.5, 0.5, -0.42, 0.9, 0.9, 1],
    [0, 0.45, 0.5, 1.9, 0.8, 0.9],
    [-0.35, 1.3, -0.3, 0.95, 0.8, 0.95],
  ] as const) {
    B('spar', dx, y + 0.05, dz, w, h, d);
    for (const s of [-1, 1]) {
      B('wale', dx + s * (w / 2 - 0.05), y + 0.05, dz, 0.1, h + 0.02, d + 0.02);
      B('wale', dx, y + 0.05, dz + s * (d / 2 - 0.05), w + 0.02, h + 0.02, 0.1);
    }
  }
  for (const s of [-1, 1]) {
    lashing(root, kit, [s * 1.05, 0.05, -1.05], [-s * 0.2, 1.75, 0], 0.025);
    lashing(root, kit, [s * 1.05, 0.05, 1.05], [-s * 0.2, 1.75, 0], 0.025);
  }
  for (const [dx, dz] of [
    [-1.05, -1.05],
    [1.05, -1.05],
    [-1.05, 1.05],
    [1.05, 1.05],
  ] as const)
    primitive(root, kit.get('iron'), new THREE.TorusGeometry(0.07, 0.02, 3, 8).rotateX(Math.PI / 2), dx, 0.03, dz);
  return root;
};
